import React, { useMemo } from "react";
import moment from "moment";

// --- Internal Analog Clock Component ---
const AnalogClock = ({ timeStr, color, faceColor = "#000" }) => {
  if (!timeStr || timeStr === "—")
    return (
      <div
        style={{
          width: "6svh",
          height: "6svh",
          borderRadius: "50%",
          border: "0.3svh solid #555",
          opacity: 0.25
        }}
      />
    );

  const m = moment(timeStr, ["h:mm A", "H:mm"]);
  if (!m.isValid()) return null;

  const minutes = m.minutes();
  const hours = m.hours();
  const minuteDeg = (minutes / 60) * 360;
  const hourDeg = ((hours % 12) / 12) * 360 + (minutes / 60) * 30;

  return (
    <div
      className="relative rounded-full flex items-center justify-center"
      style={{
        width: "8svh",
        height: "8svh",
        backgroundColor: faceColor,
        border: `0.35svh solid ${color}`,
        boxShadow: `0 0 0.6svh ${color}33`
      }}
    >
      {/* Clock Numbers */}
      {[...Array(12)].map((_, i) => {
        const num = i + 1;
        const angle = num * 30;
        const rad = (angle - 90) * (Math.PI / 180);
        const r = 40;
        const x = 50 + r * Math.cos(rad);
        const y = 50 + r * Math.sin(rad);

        return (
          <div
            key={num}
            className="absolute font-bold leading-none"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: "translate(-50%, -50%)",
              color,
              fontSize: "0.9svh",
              opacity: 0.85
            }}
          >
            {num}
          </div>
        );
      })}

      {/* Center Dot */}
      <div
        style={{
          width: "0.6svh",
          height: "0.6svh",
          backgroundColor: "red",
          borderRadius: "50%",
          position: "absolute",
          zIndex: 20
        }}
      />

      {/* Hour Hand */}
      <div
        style={{
          width: "0.35svh",
          height: "28%",
          backgroundColor: "#fff",
          position: "absolute",
          bottom: "50%",
          transformOrigin: "bottom",
          transform: `rotate(${hourDeg}deg)`,
          borderRadius: "1svh",
          zIndex: 10
        }}
      />

      {/* Minute Hand */}
      <div
        style={{
          width: "0.25svh",
          height: "42%",
          backgroundColor: color,
          position: "absolute",
          bottom: "50%",
          transformOrigin: "bottom",
          transform: `rotate(${minuteDeg}deg)`,
          borderRadius: "1svh",
          zIndex: 10
        }}
      />
    </div>
  );
};


// --- Main Panel Component ---
const PrayerTimesPanel = ({
  labelTextColor = "#ffffff",
  timingTextColor = "#84cc16",
  textColor = "#ffffff",
  highlightColor = "#facc15", // Default Yellow Highlight
  prayerData,
  currentTime
}) => {
  
  const { prayers, activeIndex } = useMemo(() => {
    if (!prayerData) return { prayers: [], activeIndex: -1 };
    
    const iqama = prayerData.iqamatimes || {};
    const adhan = prayerData.startTimes?.timings || {};

    const list = [
      { key: "fajr", urdu: "فجر", english: "Fajr", iqama: iqama.fajr, start: adhan.fajr },
      { key: "dhuhr", urdu: "ظہر", english: "Dhuhr", iqama: iqama.dhuhr, start: adhan.dhuhr },
      { key: "asr", urdu: "عصر", english: "Asr", iqama: iqama.asr, start: adhan.asr },
      { key: "maghrib", urdu: "مغرب", english: "Maghrib", iqama: iqama.maghrib, start: adhan.maghrib },
      { key: "isha", urdu: "عشاء", english: "Isha", iqama: iqama.isha, start: adhan.isha },
    ];

    // Highlight Logic
    let idx = 0; // Default to Fajr
    let fajrSkipped = false;

    if (currentTime) {
      const now = currentTime;
      let foundIndex = -1;

      for (let i = 0; i < 5; i++) {
        const p = list[i];
        const timeStr = (p.iqama && p.iqama !== "—") ? p.iqama : p.start;

        if (!timeStr || timeStr === "—") {
            if (i === 0) fajrSkipped = true;
            continue;
        }

        const target = moment(timeStr, ["h:mm A", "HH:mm"]);
        if (!target.isValid()) {
            if (i === 0) fajrSkipped = true;
            continue;
        }

        target.set({ year: now.year(), month: now.month(), date: now.date() });

        if (now.isBefore(target)) {
          foundIndex = i;
          break;
        }
      }

      if (foundIndex > 0 && fajrSkipped && now.hour() < 12) {
          idx = 0; // Force Fajr
      } else if (foundIndex !== -1) {
          idx = foundIndex;
      }
    }

    return { prayers: list, activeIndex: idx };
  }, [prayerData, currentTime]);

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === "—") return "—";
    return timeStr.replace(/(AM|PM)/i, "").trim().replace(/^0/, "");
  };

  if (!prayers.length) return <div className="text-white p-2">Loading timings...</div>;

  return (
    <div className="w-full">
      {/* Heading */}
      <div className="flex w-full items-end px-2 mb-2 font-semibold text-[3svh] opacity-80" style={{ color: labelTextColor }}>
         
         {/* Left Spacer (matches left column width) */}
         <div className="w-[30%]"></div>

         {/* Center Label: IQAMAH */}
         <div className="flex-1 text-center">
            Iqamah Clocks
         </div>

         {/* Right Label: BEGINNING TIME */}
         <div className="w-[30%] text-right">
            Beginning Time
         </div>
      </div>

      {/* Prayer Rows */}
      <div className="flex flex-col ">
        {prayers.map((p, index) => {
          const isNext = index === activeIndex;
          const iqamaFormatted = formatTime(p.iqama);
          const startFormatted = formatTime(p.start);

          return (
            <div
              key={p.key}
              // ✅ Extreme Ends Layout: justify-between pushes content to edges
              className="flex justify-between items-center py-[1svh] px-4 rounded-xl transition-all duration-300"
              style={{
                // Highlight Styling
                backgroundColor: isNext ? `${highlightColor}22` : "transparent", // 22 = low opacity hex
                border: isNext ? `1svh solid ${highlightColor}` : "1svh solid transparent"
              }}
            >
              
              {/* --- LEFT: Urdu + Iqamah (Digital) --- */}
              <div className="flex flex-col items-start pl-0 w-[30%]">
                <span 
                    className="font-sans font-bold leading-none mb-1"
                    style={{ color: textColor, fontSize: "clamp(1.2svh, 2svh, 1.5rem)" }}
                >
                  {p.urdu}
                </span>
                <span
                  className="font-[Digital-7] leading-none text-left"
                  style={{
                    fontSize: "clamp(6svh, 3.5svh, 8svh)",
                    color: timingTextColor,
                    textShadow: isNext ? `0 0 10svh ${timingTextColor}66` : "none"
                  }}
                >
                  {iqamaFormatted}
                </span>
              </div>

              {/* --- CENTER: Analog Clock --- */}
              <div className="flex-1 flex justify-center">
                <AnalogClock timeStr={p.iqama} color={timingTextColor} />
              </div>

              {/* --- RIGHT: English + Start Time --- */}
              <div className="flex flex-col items-end pr-0 w-[30%]">
                <span 
                    className="font-sans font-semibold leading-none mb-1"
                    style={{ color: textColor, fontSize: "clamp(2svh, 1.5svh, 1.2svh)" }}
                >
                  {p.english}
                </span>
                <span
                  className="font-[Digital-7] leading-none text-right"
                  style={{
                    fontSize: "clamp(6svh, 2.5svh, 4svh)",
                    color: textColor, // Start time usually white
                  }}
                >
                  {startFormatted}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PrayerTimesPanel;